import React from "react";
import { CheckSquare, Square, UserPlus, LogIn, IdCard, KeyRound, ArrowRight, Layers, AlertTriangle, Info, CalendarClock } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useDemoI18n } from "../lib/demoI18n";

// FRONTEND-ONLY "Master Enabler — get started" onboarding, prepended to every tour.
// Order (confirmed with site owner): Select app(s) -> Register -> Log in & get code
// -> Create profile -> Input code (Web: entry field / Standalone & Full Suite: Enable-code field)
// -> Continue with the enabled app(s).
// TODO(backend/PDF): add this section to manual_content.py for every app + warm 12-language
// translations so it also appears in the downloadable PDF manuals. (See MASTER_ENABLER_PDF_TODO below.)
export const MASTER_ENABLER_STEPS = [
  { rid: "me-select-apps", h: "Select a single application or all 4",
    d: "Choose one application or the full suite — you can always come back to the Master Enabler to add more later." },
  { rid: "me-register", h: "Register",
    d: "Create your account with your name and your verified email and mobile number." },
  { rid: "me-login-code", h: "Log in & get your code",
    d: "Sign in; your 20-character activation code is sent to your verified email and mobile." },
  { rid: "me-profile", h: "Create your profile",
    d: "Set your display name, preferred language and theme." },
  { rid: "me-input-code", h: "Input the code",
    d: "Web app: paste it into the code entry field. Standalone app / full suite: paste it into the Enable-code field. Enter it once to initiate." },
  { rid: "me-caveat-grace", h: "Annual service fee & 30-day grace period", silent: true, dur: 9000,
    d: "Activation carries an annual service fee. At sign-up you can opt into a 30-day grace period — start using the app right away while the fee is settled. A reminder is sent by email and SMS around day 15. If the fee is still unpaid, service is suspended on day 31 until payment; settle within the 30 days and your account keeps running without interruption." },
  { rid: "me-caveat-name", h: "Name-match check (a caveat, not a block)", silent: true, dur: 9000,
    d: "During onboarding the system compares three names — your Registration name, the name on the e-signed Agreement, and the billing name on your card. The check is deliberately lenient: it ignores case, accents, punctuation, middle names and word order. If the three do not reconcile, you will see an informational caveat in your own language to review. It is a heads-up only — it never stops you using the app." },
  { rid: "me-continue", h: "Continue with the enabled application(s)",
    d: "Your selected application(s) unlock — carry on into the app." },
];

export const CAVEAT_RIDS = ["me-caveat-grace", "me-caveat-name"];

export const MASTER_ENABLER_SECTION = "Master Enabler — get started";

const Chip = ({ label, on }) => (
  <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] ${on ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-black/15 dark:border-white/15"}`}>
    {on ? <CheckSquare size={11} /> : <Square size={11} />} {label}
  </span>
);

const Btn = ({ rid, icon: Icon, label, blue }) => (
  <span data-rid={rid} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] ${blue ? "border-transparent bg-[var(--primary)] text-white" : "border-black/15 dark:border-white/15"}`}>
    <Icon size={11} /> {label}
  </span>
);

// Irreversible "delete all customer data" confirmation — shown in the tours whenever the
// delete-customer-data step is reached. The confirm button carries `confirmRid` so the
// walkthrough hand can land on "Yes, Delete All".
export function DeleteConfirmDialog({ confirmRid }) {
  const { lang } = useApp();
  const { pane } = useDemoI18n(lang);
  const P = (k, fb) => (pane && pane[k]) || fb;
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-black/45 p-3 backdrop-blur-[1px]" data-testid="replica-delete-dialog">
      <div className="w-full max-w-[240px] rounded-lg border border-[#DA1E28]/40 bg-white p-4 text-black shadow-2xl dark:bg-neutral-900 dark:text-white">
        <div className="flex items-center gap-2 text-[#DA1E28]"><AlertTriangle size={16} /> <span className="font-head text-sm font-semibold">{P("del_title", "Delete all customer data?")}</span></div>
        <p className="mt-2 text-justify text-[12px] opacity-80">{P("del_body", "This permanently deletes all customer-loaded data for this application, but the Client Profile will remain. This action cannot be reversed.")}</p>
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-medium opacity-60">{P("del_type", "Type DELETE to confirm")}</div>
          <div data-rid="rt-delete-typed" className="flex items-center rounded-md border border-[#DA1E28]/40 px-2 py-1.5 font-mono text-[12px] tracking-widest">
            DELETE<span className="ms-0.5 inline-block h-3 w-px animate-pulse self-center bg-current" />
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-2">
          <span data-rid={confirmRid} className="inline-flex items-center justify-center gap-1 rounded-md bg-[#DA1E28] px-3 py-1.5 text-[12px] font-semibold text-white">{P("del_yes", "Yes, Delete All")}</span>
          <span className="inline-flex items-center justify-center gap-1 rounded-md border border-black/15 px-3 py-1.5 text-[12px] dark:border-white/15">{P("del_no", "No, cancel Action")}</span>
        </div>
      </div>
    </div>
  );
}

export function MasterEnablerPane({ activeRid, active } = {}) {
  const { lang } = useApp();
  const { pane, me_section } = useDemoI18n(lang);
  const P = (k, fb) => (pane && pane[k]) || fb;
  const showCaveat = CAVEAT_RIDS.includes(activeRid);
  return (
    <div className="relative rounded-xl border border-black/10 bg-white text-black shadow-lg dark:border-white/10 dark:bg-neutral-900 dark:text-white">
      <div className="flex items-center gap-2 border-b border-black/10 px-3 py-2 dark:border-white/10">
        <Layers size={14} className="text-[var(--primary)]" />
        <span className="font-head text-sm font-semibold">{me_section || MASTER_ENABLER_SECTION}</span>
      </div>
      <div className="space-y-3 p-3 text-[11px]">
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-55">1 · {P("choose_apps", "Choose your application(s)")}</div>
          <div data-rid="me-select-apps" className="flex flex-wrap gap-1">
            <Chip label="Familigi™" /><Chip label="BoxAlong™" /><Chip label="Currencida™" /><Chip label="SEBAA™" />
            <Chip label={P("full_suite_chip", "All 4 — Full Suite")} on />
          </div>
          <div className="mt-1 text-[10px] italic opacity-50">{P("come_back", "You can come back any time to add more applications.")}</div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Btn rid="me-register" icon={UserPlus} label={P("register", "Register")} blue />
          <Btn rid="me-login-code" icon={LogIn} label={P("login_code", "Log in & get code")} />
          <Btn rid="me-profile" icon={IdCard} label={P("create_profile", "Create profile")} />
        </div>
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-55">{P("activation_code", "Activation code")}</div>
          <div data-rid="me-input-code" className="flex items-center gap-2 rounded-md border border-black/15 px-2 py-2 font-mono text-[12px] tracking-widest dark:border-white/15">
            <KeyRound size={13} className="text-[var(--primary)]" /> ENGWI-ABCDE-FGHIJ-KLMNO
          </div>
          <div className="mt-1 text-[10px] opacity-55">{P("code_hint", "Web app: code entry field · Standalone app / Full Suite: Enable-code field · enter once to initiate.")}</div>
        </div>
        <div data-rid="me-continue" className="inline-flex items-center gap-1 rounded-md bg-[var(--primary)] px-3 py-1.5 text-[11px] font-medium text-white">
          {P("continue", "Continue")} <ArrowRight size={12} />
        </div>
      </div>
      {showCaveat && <CaveatNotice rid={activeRid} active={active} label={P("good_to_know", "Good to know")} />}
    </div>
  );
}

// Silent, on-screen-only onboarding caveat cards (grace period & name-match).
// Rendered as an overlay on the Master Enabler pane; text comes pre-translated from the tour.
function CaveatNotice({ rid, active, label }) {
  const Icon = rid === "me-caveat-grace" ? CalendarClock : Info;
  const heading = (active && active.h) || "";
  const body = (active && (active.d || (active.s || [])[0])) || "";
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-black/45 p-3 backdrop-blur-[1px]" data-testid={`caveat-${rid}`}>
      <div className="w-full max-w-[300px] rounded-lg border border-[var(--primary)]/40 bg-white p-4 text-black shadow-2xl dark:bg-neutral-900 dark:text-white">
        <div className="flex items-center gap-2 text-[var(--primary)]">
          <Icon size={16} /> <span className="font-head text-sm font-semibold">{heading}</span>
        </div>
        <p className="mt-2 text-justify text-[12px] leading-relaxed opacity-85">{body}</p>
        <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--primary)]">
          <Info size={11} /> {label}
        </div>
      </div>
    </div>
  );
}
